<template>
	<div class="login-wrapper">
		<!-- *********************************************
        *                Publicity Content               *
        ********************************************** -->
		<elements-to-display v-if="!getMobileAppState"/>
		<!-- *********************************************
		*												 *
        *                Login Form Content              *
		*												 *
        ********************************************** -->
		<div class="content-wrapper" :class="{ 'mobile': getMobileAppState }">
			<!-- *********************************************
			*                Regular Login Form              *
			********************************************** -->
			<regular-login
			v-if="isRegLogin"
			:username="username"
			:password="password"
			:rememberMe="rememberMe"
			:selectedAcessionType="selectedAcessionType"
			@showAcessionModal="showAcessions = true"
			@updateSelectedAcessionType="selectedAcessionType = $event"
			@updateUsername="username = $event"
			@updatePassword="password = $event"
			@updateRememberMe="rememberMe = !rememberMe"
			@loginHandler="showAuthenticationModal = true"/>
			<!-- *********************************************
			*              Remember Me Login Form            *
			********************************************** -->
			<remember-me-login
			v-if="!isRegLogin && getRememberMeEntityInfo"
			:key="forceRerender"
			:password="password"
			@updatePassword="password = $event"
			@showAcessionModal="showAcessions = true"
			@goToRegLogin="goToRegLoginHandler"
			@showFeedback="successHandler"
			@requestBiometricChallenge="requestBiometricChallenge"/>
			<!-- *********************************************
			*                  Bottom Buttons                *
			********************************************** -->
			<div class="footer-buttons-wrapper" :class="{'regular-login': isRegLogin}">
				<button-tertiary
				buttonSize="tiny"
				icon="phone"
				:label="t('login.contactsBtn')"
				@clicked="navigateByName(routeList.NON_AUTHENTICATED_CONTACTS_NAME)"/>
				<button-tertiary
				buttonSize="tiny"
				icon="currency-exchange"
				:label="t('login.exchangesBtn')"
				@clicked="navigateByName(routeList.NON_AUTHENTICATED_EXCHANGES_NAME)"/>
				<button-tertiary
				buttonSize="tiny"
				icon="pin-drop"
				:label="t('login.agencysBtn')"
				@clicked="navigateByName(routeList.NON_AUTHENTICATED_AGENCIES_NAME)"/>
			</div>
		</div>
		<!-- For Captcha -->
		<input type="hidden" name="g-recaptcha-response" :value="recaptchaToken">
	</div>
	<!-- *********************************************
	*            Acessions Modal Memorized           *
	********************************************** -->
	<acessions-modal
	v-if="showAcessions"
	:triggerClose="triggerModalClose"
	:isRegLogin="isRegLogin"
	@hideMe="hideMeHandler"
	@updateRegLogin="isRegLogin = true"
	@goToRegLogin="goToRegLoginHandler"
	@goToRememberMeLogin="goToRememberMeLoginHandler"/>

	<!-- *********************************************
	*              Authentication Modal              *
	********************************************** -->
	<modal-centered-generic
    v-if="showAuthenticationModal"
    titleIcon="lock"
    rightButtonIcon="arrow-right-full"
    :title="t('login.authenticationModal.title')"
    :text="t('login.authenticationModal.text')"
    :leftButtonText="t('login.authenticationModal.leftButtonText')"
    :rightButtonText="t('login.authenticationModal.rightButtonText')"
    @hideMe="showAuthenticationModal = false"
    @rightButtonClicked="validateCaptcha">
		<selector-radio-group
		style="margin-top: 16px"
		:radioParams="authenticationRadioParams"
		@clicked="authenticationRadioParams.map(el => el.selected = el.id === $event)"/>
	</modal-centered-generic>

	<!-- Feedback toast -->
	<toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>

    <modal-centered-generic
    v-if="showLoginAlert"
    titleIcon="info"
    leftButtonIcon="check-line"
    :title="showLoginAlertParams[showLoginAlertCode].title"
    :text="showLoginAlertParams[showLoginAlertCode].text"
    :leftButtonText="showLoginAlertParams[showLoginAlertCode].leftButtonText"
    @hideMe="showLoginAlert = false"/>

	<!-- Data Collection Modal for Apps -->
	<modal-centered-generic
    v-if="getMobileAppState && showDataCollectionModal"
	preventClickOutsideClose
    titleIcon="info"
    leftButtonIcon="check-line"
    :title="showDataCollectionModalParams.title"
    :text="showDataCollectionModalParams.text"
    :leftButtonText="showDataCollectionModalParams.leftButtonText"
    @leftButtonClicked="closeDataCollectionModal"/>

	<!-- Generic Loader -->
	<loader-generic
	v-if="showOverlayLoader"
	isOverlay/>
</template>

<script setup>
import ElementsToDisplay from '@/components/features/nonAuthenticated/login/ElementsToDisplay'
import RegularLogin from '@/components/features/nonAuthenticated/login/RegularLogin'
import RememberMeLogin from '@/components/features/nonAuthenticated/login/RememberMeLogin'
import AcessionsModal from '@/components/features/nonAuthenticated/login/AcessionsModal'
import LoaderGeneric from '@/components/generic/loaders/Generic'
/* Generic Components */
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import SelectorRadioGroup from '@/components/generic/selectors/radio/RadioGroup'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* vue */
import { nextTick, ref, computed, watch, onMounted } from 'vue'
/* Helpers */
import { navigateByName, navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'
import { addItemToLocalStorage, getItemFromLocalStorage } from '@/assets/scripts/windowStorage'
import { encryptData } from '@/assets/scripts/strings'
import { getCurrentRouteMenuItemInfo, getInitialEntry } from '@/assets/scripts/parsers/menu'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateLoginLoader', 'reRenderSvg'])

/* props received
@showRegularLogin { Boolean }
*/
const props = defineProps({
    showRegularLogin: Boolean
})

/* mapActions */
const {
	login,
	loginRememberMe,
	updateEntityRememberMeInfo,
    updateEntityRememberInfo,
	enrollBiometricLogin,
	sendLoginBiometric,
	fetchBiometricChallenge,
	updateMobileBiometric,
    removeAcessionFromEntityRememberMeInfo,
    updateSelectedEntityRememberInfo,
	requestPopupsList
} = mapActions(moduleMapper.SESSION)

const { updateShowAlertRememberMeModal, updateAppThemeMode, updateAppThemeClientType } = mapActions()
const { menuFetch, submenuFetch } = mapActions(moduleMapper.MENU)
const { getClientType, getMobileAppState } = mapGetters()
const { getRememberMeEntityInfo, getMobileBiometric, getAllCustomerInfo, getToken } = mapGetters(moduleMapper.SESSION)
const { paymentsOperatorsFetch } = mapActions(moduleMapper.PAYMENTS_SERVICES)

const username = ref(process.env.VUE_APP_MOCKS === 'true' ? '001429' : '')
const password = ref(process.env.VUE_APP_MOCKS === 'true' ? '112233' : '')
const rememberMe = ref(getMobileAppState.value)
const selectedAcessionType = ref(getClientType.value)

const showOverlayLoader = ref(false)
const showAuthenticationModal = ref(false)
const showAcessions = ref(false)
const triggerModalClose = ref(false)
const showLoginAlert = ref(false)

let isRegLogin = props.showRegularLogin || !getRememberMeEntityInfo.value || !getRememberMeEntityInfo.value.listOfAcessions.length

watch(() => password.value, (newVal, oldVal) => {
	if (password.value.length === 6 && !isRegLogin) rememberMeLoginHandler()
})

const hideMeHandler = () => {
	if (!getRememberMeEntityInfo.value) selectedAcessionType.value = getClientType.value

	showAcessions.value = false
}

const forceRerender = ref(false)
const goToRegLoginHandler = () => {
	updateAppThemeMode('system')
	triggerModalClose.value = true

	if (!isRegLogin) {
		selectedAcessionType.value = getRememberMeEntityInfo.value ? getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected) ? getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected).type.toLowerCase() : getRememberMeEntityInfo.value.listOfAcessions[0].type.toLowerCase() : getClientType.value
	}

	forceRerender.value = !forceRerender.value

	username.value = ''
	password.value = ''
	isRegLogin = true

	nextTick(() => {
		triggerModalClose.value = false
	})
}

if (!getToken.value) {
    updateAppThemeMode('system')
}

const goToRememberMeLoginHandler = () => {
	triggerModalClose.value = true
	isRegLogin = false

	nextTick(() => {
		triggerModalClose.value = false
	})
}

const authenticationRadioParams = ref([{
	id: 'ussd',
	text: t('login.authenticationModal.ussd'),
	value: 'ussd',
	selected: true
}, {
	id: 'email',
	text: t('login.authenticationModal.email'),
	value: 'email',
	selected: false
}])

/*************************************************
*                Captcha Validation              *
*************************************************/
const recaptchaToken = ref(null)
const validateCaptcha = () => {
	showAuthenticationModal.value = false
	emit('updateLoginLoader', true)

	if (process.env.VUE_APP_MOCKS === 'true') {
		setTimeout(() => { loginHandler() }, 1000)
		return
	}

    /* We need to run a different function, cuz our grecaptcha has a different script */
    if (process.env.VUE_APP_ENV === 'PPRD' || process.env.VUE_APP_ENV === 'PRD') {
        window.grecaptcha.enterprise.ready(() => {
            window.grecaptcha.enterprise.execute(process.env.VUE_APP_RECAPTCHA_KEY, { action: 'login' }).then(processToLoginAfterRecaptacha)
        })
    /* DEV & QUA */
    } else {
        window.grecaptcha.ready(() => {
            window.grecaptcha.execute(process.env.VUE_APP_RECAPTCHA_KEY, { action: 'login' }).then(processToLoginAfterRecaptacha)
        })
    }
}

const processToLoginAfterRecaptacha = token => {
    recaptchaToken.value = token
    if (getMobileBiometric.value && rememberMe.value) {
        showBiometricDialog()
    } else loginHandler()
}

const proceedToEnterApp = () => {
    if (getInitialEntry() && getInitialEntry().link !== routeList.HOME_PATH && !getMobileAppState.value) {
        navigateByPath(getInitialEntry().link)
		emit('updateLoginLoader', false)
    } else {
        !getMobileAppState.value ? navigateByPath(routeList.HOME_PATH) : navigateByPath(routeList.HOME_MOBILE_PATH)
    }
}

const selectedAcession = computed(() => {
    return getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected) || getRememberMeEntityInfo.value.listOfAcessions[0]
})

const rememberMeSelectedAcession = computed(() => {
	if (!getRememberMeEntityInfo.value) return null
	return getRememberMeEntityInfo.value?.listOfAcessions.find(el => el.selected) ?? getRememberMeEntityInfo.value.listOfAcessions[0]
})

const rememberMeToken = computed(() => {
	return rememberMeSelectedAcession.value.rememberMeToken
})

/*************************************************************
*           			                                     *
*                   	 Login Requests              		 *
*           			                                     *
*           			                                     *
*************************************************************/
const loginPayload = () => {
	let payload = {
		captchaToken: recaptchaToken.value,
		username: username.value,
		password: password.value,
		isCompany: selectedAcessionType.value === 'company',
		rememberMe: rememberMe.value,
		outOfBandValidationType: authenticationRadioParams.value.find(el => el.selected).value.toUpperCase()
	}

	if (getMobileAppState.value && window.device) {
		payload = {
			...payload,
			clientChannelDevice: {
				id: window.device.uuid,
				system: window.device.platform,
				systemVersion: window.device.sdkVersion,
				vendor: window.device.manufacturer,
				model: window.device.model
			}
		}
	}

	return payload
}

const loginResponse = ref(null)
const loginHandler = async () => {
	const payload = loginPayload()

	try {
		loginResponse.value = await login(payload)
		afterLoginRequests(true)
	} catch (error) {
        if (error.response?.data && error.response?.data.code === 'invalid_contract_status_for_login') {
            navigateByName(routeList.NON_AUTHENTICATED_PRE_ACTIVE_MODE_NAME, { processId: error.response.data.additionalData.processId })
        } else if (error.response?.data && error.response?.data.code === 'invalid_contract_status_for_login_after_credentials_reset') {
            navigateByName(routeList.NON_AUTHENTICATED_PRE_ACTIVE_MODE_RECOVER_CREDENTIALS_NAME, { processId: error.response.data.additionalData.processId })
        }
		emit('updateLoginLoader', false)
		errorHandler(error.response?.data)
	}
}

const keyPair = ref(null)
const showBiometricDialog = async () => {
	showOverlayLoader.value = true

	keyPair.value = await crypto.subtle.generateKey({
			name: 'RSA-OAEP',
			modulusLength: 2048,
			publicExponent: new Uint8Array([1, 0, 1]), // 24 bit representation of 65537
			hash: 'SHA-1'
		},
		true,
		['encrypt', 'decrypt']
	)

	var exportedPrivateKey = await crypto.subtle.exportKey('jwk', keyPair.value.privateKey)

	if (window.cordova && window.cordova.platformId === 'ios') {
		window.Fingerprint.show({
			title: t('biometricLogin.title'),
			description: t('biometricLogin.description'),
			cancelButtonTitle: t('biometricLogin.cancelButtonTitle'),
			disableBackup: true
		}, (a) => {
			window.Fingerprint.registerBiometricSecret({
				secret: JSON.stringify(exportedPrivateKey),
				title: t('biometricLogin.title'),
				description: t('biometricLogin.description'),
				cancelButtonTitle: t('biometricLogin.cancelButtonTitle'),
				disableBackup: true
			}, enrollBiometricAndLoginRequest, showBiometricDialogError)
		}, showBiometricDialogError)
	} else {
		window.Fingerprint.registerBiometricSecret({
			secret: JSON.stringify(exportedPrivateKey),
			title: t('biometricLogin.title'),
			description: t('biometricLogin.description'),
			cancelButtonTitle: t('biometricLogin.cancelButtonTitle'),
			disableBackup: true
		}, enrollBiometricAndLoginRequest, showBiometricDialogError)
	}
	showOverlayLoader.value = false
}

const enrollBiometricAndLoginRequest = async () => {
	emit('updateLoginLoader', true)
	emit('reRenderSvg')

	var exportedPublicKey = await crypto.subtle.exportKey('spki', keyPair.value.publicKey)

	const publicKey = arrayBufferToBase64String(exportedPublicKey)

	const payload = { ...loginPayload(), biometricPublicKey: publicKey }

	try {
		loginResponse.value = await enrollBiometricLogin(payload)
		afterLoginRequests(true, true)
	} catch (error) {
        if (error.response?.data && error.response?.data.code === 'invalid_contract_status_for_login') {
            navigateByName(routeList.NON_AUTHENTICATED_PRE_ACTIVE_MODE_NAME, { processId: error.response.data.additionalData.processId })
        } else if (error.response?.data && error.response?.data.code === 'invalid_contract_status_for_login_after_credentials_reset') {
            navigateByName(routeList.NON_AUTHENTICATED_PRE_ACTIVE_MODE_RECOVER_CREDENTIALS_NAME, { processId: error.response.data.additionalData.processId })
        }
		emit('updateLoginLoader', false)
		errorHandler(error.response?.data)
	}
}

/*************************************************************
*           			                                     *
*           			                                     *
*                	 Remember Me Login Requests              *
*           			                                     *
*           			                                     *
*************************************************************/
const rememberMeLoginHandler = async () => {
	emit('updateLoginLoader', true)
	emit('reRenderSvg')

	const payload = {
		token: rememberMeToken.value,
		password: password.value,
		isCompany: rememberMeSelectedAcession.value.type !== 'particular'
	}

	try {
		loginResponse.value = await loginRememberMe(payload)
		afterLoginRequests()
	} catch (error) {
		emit('updateLoginLoader', false)
		errorHandler(error.response?.data)
	}
}

/* Biometric Challenge response */
const biometricChallenge = ref(null)
const biometricChallengeResponse = ref(null)
/* Requesting Biometric Challenge for login */
const requestBiometricChallenge = async () => {
	showOverlayLoader.value = true

	const payload = {
		rememberMeToken: rememberMeToken.value,
		isCompany: rememberMeSelectedAcession.value.type !== 'particular'
	}

	try {
		const challenge = await fetchBiometricChallenge(payload)
		biometricChallenge.value = challenge.data.data
		showAnswerChallengeBiometricDialog()
	} catch (error) {
		showOverlayLoader.value = false
		errorHandler(error.response?.data)
	}
}

const showAnswerChallengeBiometricDialog = () => {
	window.Fingerprint.loadBiometricSecret({
		title: t('biometricLogin.title'),
		description: t('biometricLogin.authenticationDescription'),
		cancelButtonTitle: t('biometricLogin.usePassword'),
		disableBackup: true
	}, decryptChallenge, dismissChallengeBiometricDialog)
	showOverlayLoader.value = false
}

const dismissChallengeBiometricDialog = error => {
	if (error.code !== -108) {
		feedbackToastData.value.text = error.message
		feedbackToastData.value.buttons[1].callbackAction = showAnswerChallengeBiometricDialog
		showFeedbackToast.value = true
	}
}

/* When the user rejects biometric login we proceed to the user+pass one */
const showBiometricDialogError = error => {
	emit('reRenderSvg')
	console.log(error, 'show biometric dialog error')
    const listOfErrorsAllowedToLoginWithoutBiometric = [-108, -109, -100, -101, -104, -106, -107]

	if (error && !listOfErrorsAllowedToLoginWithoutBiometric.find(errorCode => errorCode === error.code)) {
		feedbackToastData.value.text = error && error.message ? error.message : t('genericErrorMessage')
		if (error.code === -102) feedbackToastData.value.text = t('biometricLogin.authenticationFailed')
		showFeedbackToast.value = true
		feedbackToastData.value.toastType = 'error'
		emit('updateLoginLoader', false)
	} else {
		loginHandler()
	}
}

const arrayBufferToBase64String = arrayBuffer => {
	var binary = ''
	var bytes = new Uint8Array(arrayBuffer)
	var len = bytes.byteLength
	for (var i = 0; i < len; i++) {
		binary += String.fromCharCode(bytes[i])
	}
	return btoa(binary)
}

const decryptChallenge = privateKey => {
	const binaryData = Buffer.from(biometricChallenge.value.challenge, 'base64')
	const temp = JSON.parse(privateKey)

	crypto.subtle.importKey(
		'jwk',
		temp,
		{
			name: 'RSA-OAEP',
			modulusLength: 2048,
			publicExponent: new Uint8Array([1, 0, 1]), // 24 bit representation of 65537
			hash: { name: 'SHA-1' }
		},
		true,
		['decrypt']
	).then(
		(key) => {
			crypto.subtle.decrypt(
				{ name: 'RSA-OAEP' },
				key,
				binaryData
			).then(
				(data) => {
					var enc = new TextDecoder('utf-8')
					var clearText = enc.decode(data)
					console.log(clearText, 'clearText login')
					biometricChallengeResponse.value = {
						rememberMeToken: rememberMeToken.value,
						authenticationProcessId: biometricChallenge.value.loginProcessId,
						timestamp: clearText.split('|')[0],
						nonce: clearText.split('|')[1],
                        isCompany: rememberMeSelectedAcession.value.type !== 'particular'
					}
					biometricLogin()
				}
			)
		}
	)
}

/* Login with biometric */
const biometricLogin = async () => {
	emit('updateLoginLoader', true)
	emit('reRenderSvg')

	try {
		loginResponse.value = await sendLoginBiometric(biometricChallengeResponse.value)
		afterLoginRequests()
	} catch (error) {
		emit('updateLoginLoader', false)
		errorHandler(error.response?.data)
	}
}

/*************************************************
*                                                *
*              After Login Requests              *
*                                                *
*************************************************/
const afterLoginRequests = async (isSimpleLogin, hasBiometricEnrolled) => {
    Promise.allSettled([
        mandatoryInitialRequests(isSimpleLogin, hasBiometricEnrolled),
        nonBlockingInitialRequests()
    ]).then(response => {
        /* Only if Mandatory Initial Requests have error we can't allow the user to log in */
        if (response[0].status === 'rejected') {
            emit('updateLoginLoader', false)
            errorHandler(response[0].reason.response?.data)
            return
        }

        if (getCurrentRouteMenuItemInfo(routeList.USER_AREA_DEFINITIONS_SECURE_ASSOCIATION_ENROLLMENT_PATH) &&
            !loginResponse.value.data.data.hasSecureAssociation &&
			rememberMeSelectedAcession.value &&
            rememberMeSelectedAcession.value.hasBiometricEnrolled &&
            !rememberMeSelectedAcession.value.hasSeenSecureAssociationInitialAction) {
            navigateByPath(routeList.INITIAL_ACTIONS_SHOW_SECURE_ASSOCIATION_ENROLLMENT_PATH)
            return
        }
        proceedToEnterApp()
    })
}

/*************************************************
*                 Blocking requests              *
*************************************************/
const mandatoryInitialRequests = async (isSimpleLogin, hasBiometricEnrolled) => {
    // setupClarity()

	try {
        /* Updating our store and localStorage with rememberMe info */
		if (rememberMe.value && isSimpleLogin) {
			const saveEntityResult = await updateEntityRememberMeInfo({
				requiresOutOfBandSessionAuthentication: loginResponse.value.data.data.requiresOutOfBandSessionAuthentication,
				entity: loginResponse.value.data.data.customer.bankingHolderId,
				entityName: loginResponse.value.data.data.customer.name,
				acession: {
					username: loginPayload().username,
					acessionNumber: loginResponse.value.data.data.customer.username,
					contractName: loginResponse.value.data.data.contractName,
                    customerName: loginResponse.value.data.data.customer.name,
					avatarImageUrl: loginResponse.value.data.data.customer.avatarImageUrl,
					rememberMeToken: loginResponse.value.data.data.rememberMeToken,
					type: selectedAcessionType.value,
					hasBiometricEnrolled: hasBiometricEnrolled,
                    hadSecuredAssociation: loginResponse.value.data.data.alreadyHasActiveSecureAssociation
				}
			})
			if (saveEntityResult.errorCode === 'entity_not_match_saved') updateShowAlertRememberMeModal(saveEntityResult.errorCode === 'entity_not_match_saved')
		} else if (!isRegLogin && getRememberMeEntityInfo) {
            const entityParsed = {
                ...getRememberMeEntityInfo.value,
                listOfAcessions: getRememberMeEntityInfo.value.listOfAcessions.map(accession => {
                    return {
                        ...accession,
                        customerName: loginResponse.value.data.data.customer.username === accession.acessionNumber ? loginResponse.value.data.data.customer.name : accession.customerName
                    }
                })
            }

            updateEntityRememberInfo(entityParsed)
        }
        /* requesting menu and submenu (if mobile) */
		await menuFetch()
		if (getMobileAppState.value) await submenuFetch()

        /* Updating light/dark mode based on remember me saved info in localStorage */
        if (rememberMeSelectedAcession.value && rememberMeSelectedAcession.value.selectedAppTheme) {
            updateAppThemeMode(rememberMeSelectedAcession.value.selectedAppTheme)
        } else if (getItemFromLocalStorage('bai-v4-theme-auth-mode')) updateAppThemeMode(getItemFromLocalStorage('bai-v4-theme-auth-mode').theme)
	} catch (error) {
		emit('updateLoginLoader', false)
		errorHandler(error.response?.data)
        return Promise.reject
	}
}

const setupClarity = () => {
    if (process.env.VUE_APP_ENV === 'DEV' || process.env.VUE_APP_ENV === 'PRD') {
        /* Mobile */
        if (window.cordova) {
            window.ClarityPlugin.setCustomUserId(encryptData(getAllCustomerInfo.value.acessionNumber), () => {}, () => {})
            window.ClarityPlugin.setCustomSessionId(encryptData(getToken.value), () => {}, () => {})
        } else {
            /* Web */
            window.clarity('identify', encryptData(getAllCustomerInfo.value.acessionNumber), encryptData(getToken.value))
        }
    }
}

/*************************************************
*              Non blocking requests             *
*************************************************/
const nonBlockingInitialRequests = async () => {
	try {
		await requestPopupsList()
		await paymentsOperatorsFetch()
	} finally {
		/* Do nothing */
	}
}
/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = () => {
	feedbackToastData.value = sucessObjectConstrutor(t('login.successFeedbackToast'))
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    if (data &&
		(
			data.code === 'biometric_login_not_found' ||
			data.code === 'invalid_remember_me_token' ||
			data.code === 'remember_me_token_max_tries_reached' ||
			data.code === 'biometric_remember_me_login_not_found'
		)
	) {
        removeAcessionHandler()
    }
	feedbackToastData.value = errorObjectConstructor({ ...data, dismissToastBtnText: t('login.errorFeedbackToast') }, retryFn)
	showFeedbackToast.value = true
}

const removeAcessionHandler = () => {
    removeAcessionFromEntityRememberMeInfo(selectedAcession.value.acessionNumber)

    if (getRememberMeEntityInfo.value && !getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected)) {
        updateSelectedEntityRememberInfo(getRememberMeEntityInfo.value.listOfAcessions[0].acessionNumber)
        updateAppThemeClientType(getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected).type.toLowerCase())
    }

    goToRegLoginHandler()
}

/*************************************************
*                  Biometrics               	 *
*************************************************/

const onDeviceReady = () => {
	window.Fingerprint.isAvailable(isAvailableSuccess, isAvailableError)

    if (isRegLogin) {
        updateAppThemeMode('system')
    }

	/* Revelock */
	if (process.env.VUE_APP_ENV !== 'DEV') {
		let customerId = 'Banco-bai' // For PPRD & PRD env
		if (process.env.VUE_APP_ENV === 'QUA') customerId = 'Banco-bai-qa' // For QUA env

		window.RevelockSdk.setCustomerId(customerId)

        if (isRegLogin) {
            window.RevelockSdk.registerUsernameField(document.getElementById('usernameInput'))
            window.RevelockSdk.registerPasswordField(document.getElementById('passwordInput'))
        }
	}
}

const isAvailableSuccess = (result) => {
	updateMobileBiometric(result)
}

const isAvailableError = () => {
	// 'error' will be an object with an error code and message
	updateMobileBiometric(null)
}

const showLoginAlertCode = ref(null)
const showLoginAlertParams = {
    token_not_found: {
        title: t('login.noTokenFoundModal.title'),
        text: t('login.noTokenFoundModal.text'),
        leftButtonText: t('login.noTokenFoundModal.leftButtonText')
    }
}

const showDataCollectionModal = ref(false)
const showDataCollectionModalParams = {
	title: t('login.dataCollection.title'),
	text: t('login.dataCollection.text', { link: 'https://www.bancobai.ao/media/5140/poli-tica-de-privacidade-e-de-protecc-a-o-de-dados.pdf', target: '_blank', class: 'data-collection-link' }),
	leftButtonText: t('login.dataCollection.leftButtonText')
}
const closeDataCollectionModal = () => {
	addItemToLocalStorage('hideDataCollectionModal', true)
	showDataCollectionModal.value = false
}

onMounted(() => {
    if (window.sessionStorage.getItem('showLoginAlert')) {
        showLoginAlertCode.value = window.sessionStorage.getItem('showLoginAlert')
        showLoginAlert.value = true
        window.sessionStorage.removeItem('showLoginAlert')
    }

	if (!getItemFromLocalStorage('hideDataCollectionModal')) {
		showDataCollectionModal.value = true
	}

    document.addEventListener('deviceready', onDeviceReady, false)

    const script = document.createElement('script')
    script.src = process.env.VUE_APP_RECAPTCHA_SCRIPT_URL + process.env.VUE_APP_RECAPTCHA_KEY
    script.async = true
    script.defer = true
    document.head.appendChild(script)
})
</script>

<style lang="scss" scoped>
.login-wrapper {
	width: 100%;
	height: 100%;

	display: flex;

	.content-wrapper {
		width: 100%;

		display: flex;
		flex-direction: column;
		align-items: center;
		position: relative;

		overflow: auto;
		overflow-x: hidden;

		&:not(.mobile) {
			@include generic-scrollbar(transparent, var(--primary));
            justify-content: space-between;

            .footer-buttons-wrapper {
                min-height: 120px;

                &.regular-login {
                    min-height: 182px;
                }
            }
		}

		&.mobile {
			padding-bottom: constant(safe-area-inset-bottom); /* iOS 11.0 */
			padding-bottom: env(safe-area-inset-bottom); /* iOS 11.2 */

            .footer-buttons-wrapper {
                margin: 16px 0;

                &.regular-login {
                    margin-top: 32px;
                }
            }
		}

		.footer-buttons-wrapper {
			display: flex;
            align-items: flex-end;

			gap: 16px;
			margin-bottom: 24px;

			@media only screen and (max-width: 360px) {
				gap: 8px;
			}
		}
	}
}
</style>

<style lang="scss">
.data-collection-link {
	color: var(--primary);
	text-decoration: underline;
}
</style>
